import { FieldModel } from '@cibo/core'
import { ExtendedTheme } from '@cibo/ui'
import HelpOutlineRoundedIcon from '@mui/icons-material/InfoOutlined'
import {
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { uniq } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useMultiselectCheckboxContext } from './MultiselectCheckboxContext'
import { FieldAnswer } from './MultiselectFieldInputTypes'

type MultiselectFieldInputProps = {
  row: FieldModel
  value: FieldAnswer
  disabled?: boolean
  style?: any
  options?: string[] // Optional as we will use context values if not provided
  optionsList?: { value: string; label: string }[] // Optional as we will use context values if not provided
}

export const MultiselectFieldInput = ({ row, disabled, style }: MultiselectFieldInputProps) => {
  const { t } = useTranslation('@cibo/programs/MultiselectCheckboxFieldTable')
  const theme = useTheme<ExtendedTheme>()
  const { options, traitId, year, onChangeField, fieldAnswers, optionDisplayString } =
    useMultiselectCheckboxContext()

  const value = fieldAnswers[row.resourceId] || {}

  const allOptions = uniq(['none', ...options])
  const staleOptions =
    value?.result?.filter(option => !allOptions.includes(option) && option !== 'none') ?? []

  const rowDetail = row.findDetail?.({ traitId, year })

  if (rowDetail?.immutable) {
    return (
      <Stack>
        {value?.result?.map(option => (
          <Typography data-testid="current-practices-input" color="text.secondary" key={option}>
            {optionDisplayString(option)}
          </Typography>
        ))}
      </Stack>
    )
  }

  return (
    <FormControl sx={{ m: 1, width: 300 }} style={style}>
      <Select<string[]>
        labelId="current-practices-input-label"
        id="current-practices-input"
        data-testid="current-practices-input"
        multiple
        value={fieldAnswers[row.resourceId]?.result || []}
        onChange={event => onChangeField(event, row, value)}
        input={<OutlinedInput />}
        label={t('selectPractice')}
        renderValue={selected => {
          if (fieldAnswers[row.resourceId]?.source === 'cibo') {
            return t('ciboSourcedSelect', { count: selected.length })
          }
          return selected?.length
            ? selected.map(optionDisplayString).join(', ')
            : t('selectPractice')
        }}
        disabled={disabled}
        displayEmpty
      >
        {staleOptions.map(option => (
          <MenuItem
            key={option}
            data-testid={`choice-${option}`}
            style={{
              backgroundColor:
                fieldAnswers[row.resourceId]?.result?.includes(option) &&
                fieldAnswers[row.resourceId]?.source === 'cibo'
                  ? theme.palette.ciboSensed
                  : undefined,
            }}
            value={option}
          >
            <MultiselectMenuItem
              key={option}
              typeValues={{ type: 'field', row: row }}
              stale={true}
              option={option}
            />
          </MenuItem>
        ))}
        {allOptions.map(option => (
          <MenuItem
            key={option}
            data-testid={`choice-${option}`}
            style={{
              backgroundColor:
                fieldAnswers[row.resourceId]?.result?.includes(option) &&
                fieldAnswers[row.resourceId]?.source === 'cibo'
                  ? theme.palette.ciboSensed
                  : undefined,
            }}
            value={option}
          >
            <MultiselectMenuItem
              key={option}
              typeValues={{ type: 'field', row: row }}
              stale={false}
              option={option}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export const MultiselectMenuItem = ({
  typeValues,
  stale,
  option,
  disabled = false,
  ...props
}: {
  typeValues: { type: 'field'; row: FieldModel } | { type: 'combined' }
  stale: boolean
  option: string
  disabled?: boolean
}) => {
  const { t } = useTranslation('@cibo/programs/MultiselectCheckboxFieldTable')
  const { optionDisplayString, combinedAnswer, fieldAnswers, allDetailsAreCibo } =
    useMultiselectCheckboxContext()

  const fieldDetailIsCibo =
    typeValues.type === 'field' &&
    fieldAnswers[typeValues.row.resourceId].result?.includes(option) &&
    fieldAnswers[typeValues.row.resourceId].source === 'cibo'

  const combinedDetailIsCibo =
    typeValues.type === 'combined' &&
    allDetailsAreCibo &&
    combinedAnswer[option]?.value !== 'unchecked'

  return (
    <>
      <Checkbox
        checked={
          typeValues.type === 'combined'
            ? combinedAnswer[option]?.value === 'checked'
            : fieldAnswers[typeValues.row.resourceId].result?.includes(option)
        }
        indeterminate={
          typeValues.type === 'combined' && combinedAnswer[option]?.value === 'indeterminate'
        }
        disabled={disabled}
      />
      <ListItemText
        primary={optionDisplayString(option)}
        sx={stale ? { fontStyle: 'italic', color: 'text.disabled' } : {}}
      />
      {(fieldDetailIsCibo || combinedDetailIsCibo) && (
        <Tooltip title={t('ciboSourced')}>
          <HelpOutlineRoundedIcon color="secondary" sx={{ marginLeft: '20px' }} />
        </Tooltip>
      )}
    </>
  )
}
